import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Solution } from 'src/app/shared/models/solution';
import { ConstantsService } from '../constant/constants.service';
import { Mission } from 'src/app/shared/models/mission/mission';
import { OptimCriteria } from 'src/app/shared/models/optim-criteria';
import { OptimConstraint } from 'src/app/shared/models/optim-constraint';
import { OptimizationReport } from 'src/app/shared/models/optimization-report';
import { MOptimReport } from 'src/app/shared/models/m-optim-report';
import { GenericCrudService } from '../generic-crud.service';

@Injectable({
	providedIn: 'root'
})
export class SolutionService {
	constructor(
		private appConstants: ConstantsService,
		private genericCrudService: GenericCrudService
	) {}

	getBuildSolutionByYearAndWeekNumber(
		year: number,
		weekNumber: number,
		keepPreviousAssignments: boolean
	): Observable<Solution> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.buildSolutionUrl}/${year}/${weekNumber}/${keepPreviousAssignments}`,
			'getBuildSolutionByYearAndWeekNumber'
		);
	}

	postBuildSolutionByYearAndWeekNumber(
		year: number,
		weekNumber: number,
		optimParams: { replanifDays: boolean[]; optimCriteria; optimConstraint }
	): Observable<Solution> {
		return this.genericCrudService.genericPostDataToAPI(
			`${this.appConstants.buildSolutionUrl}/${year}/${weekNumber}`,
			`postBuildSolutionByYearAndWeekNumber`,
			optimParams
		);
	}

	getSolutionByYearAndWeekNumber(
		year: number,
		weekNumber: number
	): Observable<Solution> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/${year}/${weekNumber}`,
			'getSolutionByYearAndWeekNumber'
		);
	}

	// return : {isOptimRequired: boolean, isValidationRequired: boolean})
	getSolutionInformationForDashboard(
		year: number,
		weekNumber: number
	): Observable<any> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/dashboard/${year}/${weekNumber}`,
			'getSolutionByYearAndWeekNumber'
		);
	}

	getSolutionByYearAndWeekNumberForRouteListing(
		year: number,
		weekNumber: number
	): Observable<Solution> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/${year}/${weekNumber}/listing/routes`,
			'getSolutionByYearAndWeekNumberForRouteListing'
		);
	}

	/**
	 * Renvoi la solution de la semaine weekNumber pour l'année year du centre d'id centerId
	 * @param centerId
	 * @param year
	 * @param weekNumber
	 * @returns
	 */
	getSolutionByCenterIdAndYearAndWeekNumber(
		centerId: number,
		year: number,
		weekNumber: number
	): Observable<Solution> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/${centerId}/${year}/${weekNumber}`,
			'getSolutionByCenterIdAndYearAndWeekNumber'
		);
	}

	getSolutionWithCriteria(centerId: number): Observable<OptimCriteria> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.optimUrl}/criteria/${centerId}`,
			'getSolutionWithCriteria'
		);
	}

	getSolutionWithConstraint(centerId: number): Observable<OptimConstraint> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.optimUrl}/constraint/${centerId}`,
			'getSolutionWithConstraint'
		);
	}

	getSolutionsByYearAndWeekNumber(
		year: number,
		weekNumber: number
	): Observable<Solution[]> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}s/${year}/${weekNumber}`,
			'getSolutionsByYearAndWeekNumber'
		);
	}

	/**
	 * Renvoi la liste de solutions de la semaine weekNumber pour l'année year du centre d'id centerId
	 * @param centerId
	 * @param year
	 * @param weekNumber
	 * @returns
	 */
	getSolutionsByCenterIdAndYearAndMonthNumber(
		centerId: number,
		year: number,
		monthNumber: number
	): Observable<Solution[]> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}s/${centerId}/${year}/${monthNumber}`,
			'getSolutionsByCenterIdAndYearAndMonthNumber'
		);
	}

	putSolution(solution: Solution): Observable<Solution> {
		return this.genericCrudService.genericPutDataToAPI(
			`${this.appConstants.getSolutionUrl}`,
			`putSolution`,
			solution
		);
	}

	postSolution(solution: Solution): Observable<void> {
		return this.genericCrudService.genericPostDataToAPI(
			`${this.appConstants.getSolutionUrl}`,
			`postSolution`,
			solution
		);
	}

	evaluateSolution(year: number, weekNumber: number): Observable<void> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.evaluateUrl}/${year}/${weekNumber}`,
			'evaluateSolution'
		);
	}

	validationSolution(solutionId: number): Observable<void> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/${solutionId}/validation`,
			'validationSolution'
		);
	}

	unvalidationSolution(solutionId: number): Observable<void> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/${solutionId}/unvalidation`,
			'unvalidationSolution'
		);
	}

	updateSolutionNotes(solutionId: number, notes: String): Observable<void> {
		return this.genericCrudService.genericPutDataToAPI(
			`${this.appConstants.getSolutionUrl}/${solutionId}/notes`,
			'updateSolutionNotes',
			notes
		);
	}

	deleteSolution(id: number): Observable<void> {
		return this.genericCrudService.genericDeleteAPIDataByID(
			`${this.appConstants.getSolutionUrl}/${id}`,
			`deleteSolution`
		);
	}

	addMissionToMissionNonInseree(
		solutionId: number,
		mission: Mission
	): Observable<Solution> {
		return this.genericCrudService.genericPutDataToAPI(
			`${this.appConstants.getSolutionUrl}/${solutionId}`,
			`addMissionToMissionNonInseree`,
			mission
		);
	}

	copySolutionToAnotherWeek(
		fromYear: number,
		fromWeek: number,
		toYear: number,
		toWeek: number
	): Observable<void> {
		return this.genericCrudService.genericPostDataToAPI(
			`${this.appConstants.getSolutionUrl}/copy/${fromYear}/${fromWeek}/${toYear}/${toWeek}`,
			`copySolutionToAnotherWeek`,
			null
		);
	}

	getOptimizationReport(solutionId: number): Observable<OptimizationReport> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.optimUrl}/report/${solutionId}`,
			'getOptimizationReport'
		);
	}

	getTemporaryOptimizationReports(
		solutionId: number
	): Observable<MOptimReport[]> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.optimUrl}/report/tmp/${solutionId}`,
			'getTemporaryOptimizationReports'
		);
	}

	makeOptimReportChoice(mOptimReportId: string): Observable<void> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.optimUrl}/report/tmp/choice/${mOptimReportId}`,
			'makeOptimReportChoice'
		);
	}

	isSolutionValidated(year: number, weekNumber: number): Observable<boolean> {
		return this.genericCrudService.genericGetAPIData(
			`${this.appConstants.getSolutionUrl}/state/${year}/${weekNumber}`,
			'makeOptimReportChoice'
		);
	}
}
